
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../services/supabaseClient';
import { ABMELDEN_FEHLGESCHLAGEN, abmelden, nimmSitzungsendeHinweis, tabSpeicher } from '../services/sessionRevocation';
import {
  beobachteSitzungImLauf, biometrie, biometrieStand, biometrieVerfuegbar, merkeSitzungImLauf, sitzungImLaufGesehen,
} from '../services/biometrie';
import {
  BIOMETRIE_PASSWORT_VERALTET, anmeldeFehlerArt, biometrieAnmeldungAnbieten, biometrieAutomatischFragen, biometrieEintragEntfernt,
  biometrieFehlerAus, biometrieFehlerText, biometrieNachziehen, type BiometrieStand,
} from '../services/biometrieLogik';
import { Button } from './Button';
import { Magnetic } from './ui/motion';
import { Lock, Mail, AlertCircle, Clock, Eye, EyeOff, ArrowLeft, ShieldCheck, Fingerprint } from 'lucide-react';

interface Props {
    notification?: { type: 'error' | 'success' | 'warning', message: string } | null;
}

type AuthMode = 'login' | 'signup' | 'forgot_password';

// Ein Eingabefeld, drei Modi. Vorher stand die Klassenkette viermal im JSX;
// bei jeder Aenderung driftete eines der Felder ab.
const FIELD_CLASS =
  'w-full pl-11 pr-4 py-3.5 rounded-xl text-sm text-white bg-white/[0.04] ' +
  'border border-white/[0.09] placeholder:text-slate-500 ' +
  'transition-[background-color,border-color,box-shadow] duration-300 ' +
  'hover:bg-white/[0.07] hover:border-white/[0.14] ' +
  'focus:outline-none focus:bg-white/[0.08] focus:border-blue-400/60 ' +
  'focus:shadow-[0_0_0_4px_rgba(47,131,246,0.14),0_0_28px_-6px_rgba(47,131,246,0.5)]';

const LABEL_CLASS = 'block text-[10px] font-semibold text-slate-400 uppercase tracking-[0.18em] mb-2 ml-0.5';

// Die Maske fragt hoechstens einmal je Laden der Seite von selbst nach Finger/Gesicht
// (biometrieAutomatischFragen) — auch ueber ein erneutes Einhaengen hinweg.
let biometrieAutoVersucht = false;

// … und nie, wenn es in diesem Lauf schon eine Sitzung gab (Start mit Sitzung, Anmeldung und
// spaeteres Abmelden). Abgemeldet wird in App.tsx; die Maske erfaehrt davon nur ueber diesen Merker,
// der ab dem Laden mithoert (biometrie.ts). Nur in der App mit Plugin: im Browser gibt es keine
// Biometrie. Der Supabase-Client entsteht dadurch etwas frueher als im ersten App-Effekt; in der
// Huelle traegt die Adresse nie Anmelde-Token (keine Deep Links), es geht also kein
// PASSWORD_RECOVERY verloren, und App.tsx bekommt INITIAL_SESSION wie bisher.
if (biometrieVerfuegbar()) beobachteSitzungImLauf(supabase.auth);

// Gesperrtes Konto (Benutzerverwaltung → Sperren, Migration Nr. 138): eigener Klartext.
const anmeldeFehler = (signInError: Error & { code?: string }): Error =>
  anmeldeFehlerArt(signInError) === 'gesperrt'
    ? new Error('Dieses Konto ist gesperrt. Bitte an die Benutzerverwaltung wenden.')
    : signInError;

export const AuthScreen: React.FC<Props> = ({ notification }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Animation state for success transition
  const [isSuccessAnimating, setIsSuccessAnimating] = useState(false);

  /**
   * Der Hinweis nach einem Sitzungsentzug.
   *
   * Diese Maske ist nach einem erzwungenen Reload (sessionRevocation.ts) das
   * Erste, was der Anwender sieht — ohne einen Satz dazu wirkt sie wie ein
   * Absturz. Das Merkzeichen liegt im sessionStorage, ueberlebt genau diesen
   * Reload und wird beim Abholen geloescht: der Hinweis steht einmal, nicht
   * bei jeder spaeteren Abmeldung.
   */
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);
  useEffect(() => {
      // Abholen AUSSERHALB des Setzers: der Setzer muss rein bleiben, weil
      // StrictMode ihn im Entwicklungsmodus doppelt auswertet — ein Lesen mit
      // Loeschen darin liefe zweimal. Und `if (hinweis)`, weil der Effekt
      // selbst ebenfalls doppelt laeuft: der zweite Lauf findet nichts mehr
      // und duerfte den Hinweis nicht wieder ausknipsen.
      const hinweis = nimmSitzungsendeHinweis(tabSpeicher());
      if (hinweis) setSessionNotice(hinweis);
  }, []);

  // Anmeldung mit Fingerabdruck/Gesicht: nur in der App mit Plugin `Biometrie`, im Browser bleibt das null.
  const [biometrieGeraet, setBiometrieGeraet] = useState<BiometrieStand | null>(null);
  useEffect(() => {
      let aktiv = true;
      void biometrieStand().then(s => { if (aktiv) setBiometrieGeraet(s); });
      return () => { aktiv = false; };
  }, []);

  // Sync external notifications
  useEffect(() => {
      if (notification) {
          if (notification.type === 'error' || notification.type === 'warning') {
              setError(notification.message);
              setSuccessMsg('');
          } else if (notification.type === 'success') {
              setSuccessMsg(notification.message);
              setError('');
          }
      }
  }, [notification]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMsg('');

    try {
      if (mode === 'signup') {
        if (password !== confirmPassword) throw new Error("Die Passwörter stimmen nicht überein.");

        // Registrieren geht nur mit Einladung (Migration Nr. 124): Name, Rolle
        // und Firma kommen aus der Einladung, ein eigenes Firmenfeld gibt es
        // deshalb nicht mehr. Ohne Einladung bricht der Datenbank-Trigger ab,
        // und GoTrue meldet nur „Database error saving new user“ — dieselbe
        // Meldung käme aber auch bei jedem anderen Datenbankfehler. Deshalb
        // wird die fehlende Einladung als häufigster Grund genannt, nicht als
        // sichere Diagnose.
        const { error: signUpError } = await supabase.auth.signUp({ email, password });
        if (signUpError) {
            if (/database error saving new user/i.test(signUpError.message)) {
                throw new Error('Registrierung nicht möglich. Häufigster Grund: Für diese E-Mail-Adresse liegt keine Einladung vor. Bitte genau die Adresse verwenden, an die die Einladung ging, sonst an die Benutzerverwaltung wenden.');
            }
            throw signUpError;
        }
        // Diese Abmeldung kommt aus der App selbst — kein Widerruf von aussen,
        // also kein erzwungener Reload (sessionRevocation.ts). Ohne das
        // Merkzeichen laedt der Tab hier neu und die Erfolgsmeldung zur
        // Registrierung waere weg, bevor sie jemand liest. Der Wrapper nimmt
        // es im `finally` zurueck, auch wenn dieses signOut scheitert.
        const { error: signOutError } = await abmelden(() => supabase.auth.signOut());
        // Die Registrierung ist durch — das wird in jedem Fall gesagt, sonst
        // versucht es der Anwender gleich noch einmal.
        setSuccessMsg('Registrierung erfolgreich! Bitte den Bestätigungslink in der E-Mail öffnen, danach anmelden.');
        if (signOutError) {
            // `signOut` gibt bei einem Netzfehler `{ error }` zurueck, BEVOR
            // es die Sitzung abbaut: das frisch angelegte Konto ist dann noch
            // angemeldet. Das gehoert auf den Schirm.
            setError(ABMELDEN_FEHLGESCHLAGEN);
        } else {
            setMode('login');
        }
      } else if (mode === 'forgot_password') {
        if (!email) throw new Error("E-Mail-Adresse fehlt.");
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: window.location.origin,
        });
        if (resetError) throw resetError;
        setSuccessMsg('Link zum Zurücksetzen gesendet.');
        setMode('login');
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw anmeldeFehler(signInError);
        merkeSitzungImLauf();

        // Fingerabdruck/Gesicht: ein geaendertes Passwort still nachziehen — nur fuer das
        // dort gespeicherte Konto, sonst lehnt das Plugin ohne Abfrage ab.
        if (biometrieNachziehen(biometrieGeraet, email)) {
            biometrie().speichern({ email, passwort: password, bestaetigen: false })
                .catch(e => console.warn('[AuthScreen] Biometrie-Passwort nicht nachgezogen:', e));
        }

        // --- TRIGGER SUCCESS ANIMATION ---
        setIsSuccessAnimating(true);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /** Fingerabdruck/Gesicht → gespeicherte E-Mail und Passwort → normale Anmeldung. */
  const mitBiometrieAnmelden = async () => {
    if (loading || isSuccessAnimating) return;
    biometrieAutoVersucht = true;
    setLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      let daten: { email: string; passwort: string };
      try {
        daten = await biometrie().lesen();
      } catch (e) {
        const fehler = biometrieFehlerAus(e);
        const text = biometrieFehlerText(fehler);
        if (text) setError(text);
        // Unlesbarer Eintrag bzw. geaenderte Biometrie: im Plugin schon geloescht — Knopf ausblenden.
        if (biometrieEintragEntfernt(fehler)) setBiometrieGeraet(await biometrieStand());
        return;
      }
      setEmail(daten.email);
      const { error: signInError } = await supabase.auth.signInWithPassword({ email: daten.email, password: daten.passwort });
      if (signInError) {
        throw anmeldeFehlerArt(signInError) === 'falsche_daten' ? new Error(BIOMETRIE_PASSWORT_VERALTET) : anmeldeFehler(signInError);
      }
      merkeSitzungImLauf();
      setIsSuccessAnimating(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  // Von selbst fragen (Kaltstart, Neuladen nach Sitzungsende, Rueckkehr in die App) — die
  // Regel steht in biometrieAutomatischFragen; nach einem Abmelden in diesem Lauf nie. Der Ref
  // haelt die aktuelle Funktion, damit der Effekt nur am Angebot haengt und nicht bei jedem
  // Render neu laeuft.
  const biometrieAnbieten = mode === 'login' && biometrieAnmeldungAnbieten(biometrieGeraet);
  const biometrieAnmeldenRef = useRef(mitBiometrieAnmelden);
  useEffect(() => { biometrieAnmeldenRef.current = mitBiometrieAnmelden; });
  useEffect(() => {
    if (!biometrieAnbieten) return;
    const versuchen = (anlass: 'start' | 'zurueck') => {
      if (document.visibilityState !== 'visible') return;
      if (!biometrieAutomatischFragen({
        anbieten: true, schonVersucht: biometrieAutoVersucht, sitzungImLauf: sitzungImLaufGesehen(), anlass, msSeitStart: performance.now(),
      })) return;
      biometrieAutoVersucht = true;
      void biometrieAnmeldenRef.current();
    };
    versuchen('start');
    const beiSichtbar = () => versuchen('zurueck');
    document.addEventListener('visibilitychange', beiSichtbar);
    return () => document.removeEventListener('visibilitychange', beiSichtbar);
  }, [biometrieAnbieten]);

  const isApprovalError = error.includes("freigeschaltet") || error.includes("Geduld");

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden bg-slate-950 font-sans grain">
      {/* Video (unveraendert in der Helligkeit — die Gradation liegt in den
          Ebenen darueber, damit das Original als Quelle erhalten bleibt). */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover z-0"
      >
        <source src="https://otkrfgysfhohefploofg.supabase.co/storage/v1/object/public/static-assets/homepage_GefAS.mp4" type="video/mp4" />
      </video>

      {/* Gradation in drei Ebenen statt einer.
          1. Vignette: dunkelt die Raender, haelt den Blick in der Mitte.
          2. Kaltzug: legt Blau in die Tiefen, damit Video und Marke dieselbe
             Temperatur haben — sonst wirkt die Karte aufgeklebt.
          3. Fusszug: traegt die Fusszeile, die sonst auf hellen Videobildern
             verschwindet.
          Ohne diese Ebenen haengt der Kontrast jedes Labels davon ab, welches
          Bild gerade laeuft: mal 12:1, mal 1.5:1. */}
      <div aria-hidden className="absolute inset-0 z-[1] pointer-events-none"
        style={{ background: 'radial-gradient(125% 95% at 50% 42%, rgba(2,6,23,0.34) 0%, rgba(2,6,23,0.72) 58%, rgba(2,6,23,0.94) 100%)' }} />
      <div aria-hidden className="absolute inset-0 z-[1] pointer-events-none mix-blend-color"
        style={{ background: 'linear-gradient(180deg, rgba(30,58,138,0.55), rgba(8,13,25,0.75))' }} />
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-1/3 z-[1] pointer-events-none"
        style={{ background: 'linear-gradient(to top, rgba(2,6,23,0.92), transparent)' }} />

      {/* Zwei Lichtbahnen, die langsam durchs Bild ziehen. Sie geben der
          Flaeche Bewegung, ohne dass etwas blinkt — der Blick hat nichts zu
          verfolgen, die Szene wirkt trotzdem nicht eingefroren. */}
      <div aria-hidden className="absolute inset-0 z-[2] overflow-hidden pointer-events-none">
        <div className="beam absolute -top-1/4 h-[150%] w-[26rem] blur-3xl"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(96,165,250,0.22), transparent)', animation: 'beam-sweep 19s ease-in-out infinite' }} />
        <div className="beam absolute -top-1/4 h-[150%] w-[18rem] blur-3xl"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(45,212,191,0.16), transparent)', animation: 'beam-sweep 26s ease-in-out infinite', animationDelay: '7s' }} />
      </div>

      {/* Transitioning Brand Text */}
      <div
        className={`fixed z-[100] transition-all duration-1000 ease-in-out pointer-events-none ${
            isSuccessAnimating
            ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 scale-[5] opacity-100'
            : 'static opacity-0 scale-100'
        }`}
      >
        <h2 className="text-white font-black tracking-tighter">OS:IA</h2>
      </div>

      {/* Powered by Logo (Bottom Left) - GefAS Logo */}
      <div className={`fixed bottom-8 left-8 z-[5] hidden sm:flex flex-col items-start transition-all duration-700 ease-in-out ${isSuccessAnimating ? 'opacity-0 -translate-x-10' : 'opacity-100'}`}>
          <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-[0.24em] mb-3 ml-0.5">Powered by</span>
          <div className="flex items-center gap-4">
              <img
                src="https://otkrfgysfhohefploofg.supabase.co/storage/v1/object/public/static-assets/Logo.png"
                alt="GefAS Logo"
                className="h-8 w-auto object-contain filter drop-shadow-[0_0_10px_rgba(255,255,255,0.35)]"
              />
              <div className="w-px h-6 bg-white/20"></div>
              <span className="text-[11px] text-slate-300 font-medium leading-tight uppercase tracking-[0.14em]">Intelligent<br/>Safety.</span>
          </div>
      </div>

      {/* Geteilte Komposition. Eine 27rem-Karte allein in der Mitte laesst auf
          einem breiten Schirm vier Fuenftel der Flaeche leer — die Maske wirkt
          dadurch nicht ruhig, sondern unfertig. Ab lg traegt die linke Spalte
          die Aussage, die Karte rueckt nach rechts und bekommt ein Gegenueber.
          Darunter bleibt es bei der zentrierten Karte. */}
      <div className={`relative z-10 w-full max-w-6xl grid lg:grid-cols-[1fr_27rem] gap-x-20 gap-y-12 items-center justify-items-center lg:justify-items-stretch transition-all duration-700 ease-in-out ${isSuccessAnimating ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}>

        {/* ── Aussage (ab lg) ── */}
        <div className="hidden lg:block stagger">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-200 bg-blue-500/10 ring-1 ring-inset ring-blue-400/25">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
            Sicherheits&shy;koordination
          </span>

          <h2 className="mt-7 text-[3.4rem] leading-[1.03] font-bold text-white tracking-[-0.035em]">
            Die Baustelle<br />
            <span className="text-gradient">im Griff.</span>
          </h2>

          <p className="mt-6 text-[15px] leading-relaxed text-slate-300 max-w-[34ch]">
            Von der Begehung bis zur Rechnung — ein Werkzeug für den
            gesamten Koordinationsauftrag.
          </p>

          <ul className="mt-9 space-y-3.5">
            {[
              'Begehungen dokumentieren, prüfen und versenden',
              'Gefährdungen bewerten, SiGe-Plan erzeugen',
              'Leistungen erfassen, Rechnungen stellen',
            ].map(t => (
              <li key={t} className="flex items-center gap-3 text-[13px] text-slate-300">
                <span className="w-5 h-5 shrink-0 rounded-md bg-white/[0.06] ring-1 ring-inset ring-white/10 flex items-center justify-center">
                  <ShieldCheck className="w-3 h-3 text-blue-300" />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </div>

        {/* ── Karte ── */}
        <div className="w-full max-w-[27rem]">

        <div className="edge-light rounded-[1.4rem] p-px shadow-[0_40px_120px_-30px_rgba(0,0,0,0.85)] animate-scale-in">
          <div className="relative rounded-[1.35rem] px-9 py-10 bg-slate-950/85 backdrop-blur-2xl backdrop-saturate-150 hairline-light overflow-hidden">

            {/* Lichtkante oben: laesst die Karte gewoelbt statt flach wirken. */}
            <div aria-hidden className="absolute inset-x-9 top-0 h-px bg-gradient-to-r from-transparent via-white/45 to-transparent" />

            {/* ── Marke ── */}
            <div className={`text-center mb-9 transition-opacity duration-300 ${isSuccessAnimating ? 'opacity-0' : 'opacity-100'}`}>
              <h1 className="text-[3.25rem] leading-[0.9] font-black text-white tracking-[-0.05em]">
                OS<span className="text-blue-400">:</span>IA
              </h1>
              {/* Feine Linie zwischen Wortmarke und Claim. Sie ersetzt einen
                  Abstand durch eine Kante — das liest sich gesetzt statt
                  gestapelt. */}
              <div className="flex items-center justify-center gap-3 mt-4">
                <span className="h-px w-8 bg-gradient-to-r from-transparent to-white/25" />
                <span className="text-[10px] font-semibold text-slate-300 uppercase tracking-[0.3em]">Intelligent Safety</span>
                <span className="h-px w-8 bg-gradient-to-l from-transparent to-white/25" />
              </div>
            </div>

            <form onSubmit={handleAuth} className="space-y-5">
              {/* Gleiche Kiste wie der amber-Hinweis „Wartet auf Freigabe" —
                  kein eigenes Design, nur ein anderer Anlass. */}
              {sessionNotice && (
                <div className="p-4 text-xs rounded-xl flex items-start border bg-amber-500/[0.12] text-amber-200 border-amber-400/25 animate-in slide-in-from-top-1 fade-in">
                    <Clock className="w-4 h-4 mr-3 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <span className="font-bold block mb-0.5 uppercase tracking-wider">Sitzung beendet</span>
                        <span className="leading-relaxed">{sessionNotice}</span>
                    </div>
                </div>
              )}

              {error && (
                <div className={`p-4 text-xs rounded-xl flex items-start border animate-in slide-in-from-top-1 fade-in ${
                    isApprovalError
                    ? 'bg-amber-500/[0.12] text-amber-200 border-amber-400/25'
                    : 'bg-red-500/[0.12] text-red-200 border-red-400/25'
                }`}>
                    {isApprovalError ? (
                        <Clock className="w-4 h-4 mr-3 shrink-0 mt-0.5" />
                    ) : (
                        <AlertCircle className="w-4 h-4 mr-3 shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                        <span className="font-bold block mb-0.5 uppercase tracking-wider">
                            {isApprovalError ? 'Wartet auf Freigabe' : 'Fehler'}
                        </span>
                        <span className="leading-relaxed">{error}</span>
                    </div>
                </div>
              )}

              {successMsg && (
                <div className="p-4 bg-emerald-500/[0.12] text-emerald-200 border border-emerald-400/25 text-xs rounded-xl flex items-start animate-in slide-in-from-top-1 fade-in">
                  <div className="w-4 h-4 mr-3 shrink-0 mt-0.5 rounded-full border border-emerald-300 flex items-center justify-center font-bold">✓</div>
                  <div className="flex-1">
                      <span className="font-bold block mb-0.5 uppercase tracking-wider">Erfolgreich</span>
                      <span className="leading-relaxed">{successMsg}</span>
                  </div>
                </div>
              )}

              {mode === 'signup' && (
                  <p className="text-xs leading-relaxed text-slate-400 animate-in fade-in slide-in-from-bottom-2">
                      Registrieren geht nur mit Einladung. Bitte genau die E-Mail-Adresse verwenden, an die die Einladung ging.
                  </p>
              )}

              <div>
                <label className={LABEL_CLASS} htmlFor="auth-email">E-Mail Adresse</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                  <input
                    id="auth-email"
                    type="email"
                    required
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={FIELD_CLASS}
                    placeholder="name@firma.de"
                  />
                </div>
              </div>

              {mode !== 'forgot_password' && (
                  <>
                    <div className="animate-in fade-in slide-in-from-bottom-2">
                      <label className={LABEL_CLASS} htmlFor="auth-password">Passwort</label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                        <input
                          id="auth-password"
                          type={showPassword ? "text" : "password"}
                          required
                          minLength={6}
                          name="password"
                          autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className={`${FIELD_CLASS} pr-12`}
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-white/10 transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {mode === 'login' && (
                          <div className="flex justify-end">
                              <button
                                  type="button"
                                  onClick={() => { setMode('forgot_password'); setError(''); setSuccessMsg(''); }}
                                  className="text-[10px] font-semibold text-slate-400 hover:text-blue-300 transition-colors uppercase tracking-[0.16em] mt-2.5"
                              >
                                  Passwort vergessen?
                              </button>
                          </div>
                      )}
                    </div>

                    {mode === 'signup' && (
                      <div className="animate-in fade-in slide-in-from-bottom-2">
                        <label className={LABEL_CLASS} htmlFor="auth-password-confirm">Passwort wiederholen</label>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                          <input
                            id="auth-password-confirm"
                            type={showPassword ? "text" : "password"}
                            required
                            minLength={6}
                            name="confirmPassword"
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className={FIELD_CLASS}
                            placeholder="••••••••"
                          />
                        </div>
                      </div>
                    )}
                  </>
              )}

              {/* Der einzige magnetische Punkt der Maske. Der Effekt wirkt nur,
                  solange er selten ist. */}
              <Magnetic strength={4} className="pt-1">
                <Button
                  variant="primary"
                  type="submit"
                  loading={loading}
                  disabled={isSuccessAnimating}
                  className="glare w-full py-4 text-[13px] font-bold uppercase tracking-[0.22em] rounded-xl shadow-[0_12px_36px_-10px_rgba(29,99,224,0.75)]"
                >
                  {loading ? 'Prüfe…' : (
                      mode === 'login' ? 'Anmelden' :
                      mode === 'signup' ? 'Konto erstellen' :
                      'Reset anfordern'
                  )}
                </Button>
              </Magnetic>

              {biometrieAnbieten && (
                <div className="space-y-2 animate-in fade-in">
                  <button
                    type="button"
                    onClick={() => void mitBiometrieAnmelden()}
                    disabled={loading || isSuccessAnimating}
                    className="w-full min-h-[44px] py-3.5 rounded-xl border border-white/[0.14] bg-white/[0.04] text-white text-[12px] font-semibold uppercase tracking-[0.16em] flex items-center justify-center gap-2 hover:bg-white/[0.08] transition-colors disabled:opacity-50"
                  >
                    <Fingerprint className="w-4 h-4 text-blue-300" /> Fingerabdruck / Gesicht
                  </button>
                  <p className="text-[11px] text-slate-400 text-center">als {biometrieGeraet?.email}</p>
                </div>
              )}

              <div className="text-center pt-6 border-t border-white/[0.07]">
                {mode === 'login' ? (
                    <button
                        type="button"
                        onClick={() => { setMode('signup'); setError(''); setSuccessMsg(''); }}
                        className="text-xs text-slate-400 hover:text-slate-200 font-medium transition-colors"
                    >
                        Eingeladen? <span className="text-blue-300 font-semibold">Jetzt registrieren</span>
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={() => { setMode('login'); setError(''); setSuccessMsg(''); }}
                        className="text-xs text-slate-400 hover:text-slate-200 font-medium transition-colors flex items-center justify-center gap-2 w-full"
                    >
                        <ArrowLeft className="w-3 h-3" /> Zurück zum Login
                    </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Vertrauensanker unter der Karte. Eine Anmeldemaske ohne jeden Hinweis
            auf die Verbindung wirkt beliebig; die Zeile kostet nichts und
            beantwortet die Frage, bevor sie gestellt wird. */}
        <div className="mt-5 flex items-center justify-center gap-2 text-[10px] text-slate-500 uppercase tracking-[0.2em] font-medium">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-500/70" />
          Verschlüsselte Verbindung
        </div>
        </div>
      </div>
    </div>
  );
};
